import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import useLenis from "@/hooks/useLenis";
import OrderForm from "@/components/OrderForm";

const CHAPTERS = [
  { n: "01", over: "Proudly Kid-Owned", title: "A young baker, a big dream.", body: "CakesbyaKid is owned and run by a kid with a serious passion for baking. Every order supports a young entrepreneur learning the craft — one cake at a time." },
  { n: "02", over: "Slow by Design", title: "Two weeks of notice, zero shortcuts.", body: "Real butter, real fruit, hand-piped detail. Craft can't be rushed, so we ask for a fortnight to make yours exactly right." },
  { n: "03", over: "Cash on Pickup", title: "Simple, honest, no card required.", body: "Reserve your cake online, pay in cash when you collect it from the counter. Warm, fresh, and yours." },
];

function RevealLine({ children, delay = 0 }) {
  return (
    <span className="line-mask">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function FadeUp({ children, className, delay = 0 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 40, filter: "blur(6px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  useLenis();

  return (
    <div className="relative bg-background min-h-screen text-primary">
      <div className="noise-overlay" />

      {/* Nav */}
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-6">
        <span className="font-serif text-2xl tracking-tight" data-testid="brand-logo">CakesbyaKid</span>
        <nav className="flex items-center gap-8 text-sm">
          <a href="#story" className="hidden sm:inline text-secondary hover:text-primary transition-colors" data-testid="nav-story">Our Story</a>
          <a href="#order" data-testid="nav-order" className="btn-sweep border border-primary text-primary hover:text-primary-foreground px-5 py-2 rounded-sm transition-colors">
            Order a Cake
          </a>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative z-20 px-6 sm:px-10 pt-10 pb-24">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <FadeUp>
              <span className="text-xs uppercase tracking-[0.3em] text-accent">Bespoke Cakes · Proudly Kid-Owned</span>
            </FadeUp>
            <h1 className="font-serif font-medium leading-[0.92] tracking-tight text-6xl sm:text-7xl lg:text-8xl mt-6">
              <RevealLine delay={0.1}>Cakes worth</RevealLine>
              <RevealLine delay={0.22}><span className="italic text-accent">the wait.</span></RevealLine>
            </h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-secondary text-lg max-w-md mt-8 leading-relaxed"
            >
              Describe the cake of your dreams — flavor, size, occasion — and our young baker brings it to life. Just 3 cakes a week, two weeks' notice, cash on pickup.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.85, duration: 0.8 }}
              className="mt-10 flex items-center gap-6"
            >
              <a href="#order" data-testid="hero-order-btn" className="btn-sweep bg-primary text-primary-foreground hover:text-primary-foreground px-8 py-4 rounded-sm text-base inline-flex items-center gap-2 transition-colors">
                Start your order <ArrowUpRight size={18} />
              </a>
              <a href="#story" className="text-sm text-secondary hover:text-primary transition-colors inline-flex items-center gap-2">
                <ArrowDown size={16} /> How it works
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-sm aspect-[3/4] border border-border bg-accent text-accent-foreground overflow-hidden flex flex-col justify-between p-6 sm:p-7"
              data-testid="hero-panel"
            >
              <div className="flex items-start justify-between text-xs uppercase tracking-[0.3em] text-accent-foreground/80">
                <span>Est. 2026</span>
                <span>Kid-Owned</span>
              </div>

              <div className="flex-1 flex items-center justify-center py-5">
                <img
                  src="https://customer-assets-m6fa6gv7.emergentagent.net/job_cake-order-hub-50/artifacts/kjksv3mv_Celebration%20Stack.webp"
                  alt="Watercolor celebration cake with pastel roses"
                  className="w-full max-w-[86%] aspect-square object-cover rounded-sm border-[3px] border-white/95 shadow-2xl"
                />
              </div>

              <div>
                <p className="font-serif text-2xl leading-snug mb-5">Baked to order,<br />never off a shelf.</p>
                <div className="grid grid-cols-3 gap-4 border-t border-accent-foreground/30 pt-4">
                  <div><p className="font-serif text-2xl text-highlight">3</p><p className="text-[11px] uppercase tracking-widest text-accent-foreground/85">cakes / week</p></div>
                  <div><p className="font-serif text-2xl text-highlight">2wk</p><p className="text-[11px] uppercase tracking-widest text-accent-foreground/85">notice</p></div>
                  <div><p className="font-serif text-2xl text-highlight">Cash</p><p className="text-[11px] uppercase tracking-widest text-accent-foreground/85">on pickup</p></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="relative z-20 border-y border-border py-5 bg-background">
        <Marquee speed={38} gradient={false} className="overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="font-serif italic text-3xl sm:text-4xl text-secondary/70 mx-8">
              Bespoke Cakes — Cash on Pickup — Local Ingredients — Two Week Notice —
            </span>
          ))}
        </Marquee>
      </section>

      {/* Manifesto chapters */}
      <section id="story" className="relative z-20 px-6 sm:px-10 py-24 sm:py-32 max-w-6xl">
        <FadeUp>
          <span className="text-xs uppercase tracking-[0.3em] text-accent">The Craft</span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-4 max-w-2xl leading-tight">A little bakery, run by a kid who says yes to your imagination.</h2>
        </FadeUp>
        <div className="mt-16 sm:mt-24 space-y-16 sm:space-y-24">
          {CHAPTERS.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start border-t border-border pt-10">
                <div className="md:col-span-3">
                  <span className="font-serif text-6xl sm:text-7xl text-accent/90">{c.n}</span>
                </div>
                <div className="md:col-span-9 max-w-2xl">
                  <span className="text-xs uppercase tracking-[0.3em] text-secondary">{c.over}</span>
                  <h3 className="font-serif text-3xl sm:text-4xl mt-3 leading-snug">{c.title}</h3>
                  <p className="text-secondary text-lg mt-4 leading-relaxed">{c.body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Notice banner */}
      <section className="relative z-20 px-6 sm:px-10 py-6">
        <FadeUp>
          <div className="bg-accent text-accent-foreground rounded-sm px-8 py-8 sm:py-10 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="notice-banner">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] opacity-80 mb-2">Please read before ordering</p>
              <p className="font-serif text-2xl sm:text-3xl max-w-2xl leading-snug">
                We bake only three cakes a week and ask for two weeks' notice.
              </p>
            </div>
            <span className="font-serif text-6xl sm:text-7xl opacity-30 shrink-0">3/wk</span>
          </div>
        </FadeUp>
      </section>

      {/* Order form */}
      <section id="order" className="relative z-20 px-6 sm:px-10 py-16 sm:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-16">
              <FadeUp>
                <span className="text-xs uppercase tracking-[0.3em] text-accent">Build your cake</span>
                <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Describe<br />the cake you<br />want.</h2>
                <p className="text-secondary text-lg mt-6 leading-relaxed max-w-sm">
                  Use the menus below to tell us the essentials, then add every little detail. We'll confirm by email within 48 hours.
                </p>
                <p className="text-secondary text-sm mt-8 leading-relaxed max-w-sm">
                  Payment is <span className="text-primary font-medium">cash on pickup</span>. No deposit or card required to reserve.
                </p>
              </FadeUp>
            </div>
          </div>
          <div className="lg:col-span-8">
            <FadeUp delay={0.1}>
              <OrderForm />
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-20 border-t border-border px-6 sm:px-10 py-12 flex flex-col sm:flex-row justify-between gap-6">
        <div>
          <span className="font-serif text-2xl">CakesbyaKid</span>
          <p className="text-secondary text-sm mt-2 max-w-xs">Proudly kid-owned. Small-batch custom cakes, baked with patience and butter.</p>
        </div>
        <div className="text-sm text-secondary flex flex-col sm:items-end gap-1">
          <a href="mailto:cakemaster@cakesbyakid.com" className="text-primary hover:text-accent transition-colors" data-testid="contact-email">cakemaster@cakesbyakid.com</a>
          <span>Cash on pickup · Two weeks' notice</span>
        </div>
      </footer>
    </div>
  );
}
